import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { noop } from '../utils';

const MAXIMUM_NEGATIVE = -60 * 100 + 1; // -99:59

export const getFormattedTimeLeft = (timeLeft, allowNegative = false) => {
  const sign = timeLeft < 0 && allowNegative ? '-' : '';
  const absoluteTime = Math.abs(timeLeft);

  const minutes = Math.floor(absoluteTime / 60);
  const seconds = absoluteTime - minutes * 60;
  return `${sign}${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
};

/**
 *
 * @callback getCountDownValue
 * @returns {number} value in seconds
 */

/**
 *
 * @param {getCountDownValue} getCountDownValue - function that returns countdown value in seconds
 * @param {boolean} allowNegative - allow negative countdown
 * @returns {{formattedTime: string, countTime: number}} - formatted time and actual counter state
 */
const useCountDown = ({ getCountDownValue, allowNegative = false, enabled = true }) => {
  const [count, setCount] = useState(getCountDownValue());

  const reset = (newCount: number) => setCount(newCount);

  useEffect(() => {
    if (!enabled) {
      return noop;
    }
    setCount(getCountDownValue());
    const intervalId = setInterval(() => {
      const newValue = getCountDownValue();
      setCount(newValue);
      if ((newValue <= 0 && !allowNegative) || newValue < MAXIMUM_NEGATIVE) {
        clearInterval(intervalId);
      }
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [getCountDownValue, enabled]);

  const formattedTime = getFormattedTimeLeft(count, allowNegative);

  return { formattedTime, countTime: count, reset };
};

useCountDown.propTypes = {
  time: PropTypes.number,
  allowNegative: PropTypes.bool
};
useCountDown.defaultProps = {
  time: 0
};

export default useCountDown;
